const chapter = {
  id: 'aldehydes-ketones-acids',
  slug: 'aldehydes-ketones-acids',
  subject: 'chemistry',
  subjectLabel: 'Chemistry',
  exam: 'JEE Main + NEET',
  title: {
    english: 'Aldehydes, Ketones & Carboxylic Acids',
        hindi: 'एल्डिहाइड, कीटोन और कार्बोक्सिलिक अम्ल',
  },
  estReadMinutes: 60,
  difficulty: {
    english: 'Medium-Hard',
        hindi: 'मध्यम-कठिन',
  },
  weightage: {
    english: '3 questions in JEE Main / NEET',
        hindi: 'जेईई मेन/एनईईटी में 3 प्रश्न',
  },
  syllabusTags: ['Organic Chemistry', 'JEE-Main', 'NEET', 'Class-12'],
  sections: [
    {
      id: 'structure',
      title: {
        english: 'Structure of Carbonyl Group',
        hindi: 'कार्बोनिल समूह की संरचना',
      },
      blocks: [
        {
          type: 'list',
          items: [
            {
              english: 'C=O: $sp^2$ C, planar; bond length ≈ 1.22 Å, bond energy ≈ 749 kJ/mol; very polar ($\\delta+$ on C, $\\delta-$ on O).',
        hindi: 'C=O: C, तलीय; बांड की लंबाई ≈ 1.22 Å, बांड ऊर्जा ≈ 749 kJ/mol; बहुत ध्रुवीय (C पर $\\delta+$, O पर $\\delta-$)।',
            },
            {
              english: 'BP higher than alkanes / ethers (dipole-dipole) but lower than alcohols (no H-bonding).',
        hindi: 'बीपी अल्केन्स/ईथर (डिपोल-डिपोल) से अधिक लेकिन अल्कोहल से कम (कोई एच-बॉन्डिंग नहीं)।',
            },
            {
              english: 'Lower aldehydes/ketones soluble in water (H-bond accept); solubility falls with chain length.',
        hindi: 'निचला aldehydes/ketones पानी में घुलनशील (H-बॉन्ड स्वीकार); घुलनशीलता श्रृंखला की लंबाई के साथ गिरती है।',
            },
          ],
        },
      ],
    },
    {
      id: 'preparation',
      title: {
        english: 'Preparation of Aldehydes & Ketones',
        hindi: 'एल्डिहाइड और कीटोन की तैयारी',
      },
      blocks: [
        {
          type: 'list',
          items: [
            {
              english: '**From alcohols**: 1° + PCC → aldehyde; 2° + PCC/Jones → ketone. Cu/573 K dehydrogenation.',
        hindi: '**अल्कोहल से**: 1° + पीसीसी → एल्डिहाइड; 2° + PCC/Jones → कीटोन। Cu/573 K डिहाइड्रोजनीकरण।',
            },
            {
              english: '**From alkenes**: ozonolysis ($O_3$ then $Zn/H_2O$) — reductive cleavage to carbonyls.',
        hindi: '**एल्केन्स से**: ओजोनोलिसिस ($O_3$ फिर $Zn/H_2O$) - कार्बोनिल्स में रिडक्टिव क्लीवेज।',
            },
            {
              english: '**From alkynes**: hydration with $H_2SO_4/HgSO_4$ — Markovnikov; HC≡CH → CH₃CHO; R-C≡CH → ketone.',
        hindi: '**अल्कीन्स से**: $H_2SO_4/HgSO_4$ के साथ जलयोजन - मार्कोवनिकोव; HC≡CH → CH₃CHO; R-C≡CH → कीटोन।',
            },
            {
              english: '**Rosenmund**: $RCOCl + H_2 \\xrightarrow{Pd/BaSO_4,\\ S} RCHO$ (poisoned catalyst stops at aldehyde).',
        hindi: '**रोसेनमुंड**: $RCOCl + H_2 \\xrightarrow{Pd/BaSO_4,\\\\ S} RCHO$ (जहरीला उत्प्रेरक एल्डिहाइड पर रुकता है)।',
            },
            {
              english: '**Stephen**: $RCN + SnCl_2/HCl$ → imine → $RCHO$ on hydrolysis.',
        hindi: '**स्टीफ़न**: $RCN + SnCl_2/HCl$ → इमाइन → $RCHO$ हाइड्रोलिसिस पर।',
            },
            {
              english: '**DIBAL-H**: reduces RCN, esters at low T → RCHO.',
        hindi: '**DIBAL-H**: कम T → RCHO पर RCN, एस्टर को कम करता है।',
            },
            {
              english: '**From acid chloride / nitrile + RMgX or R₂CuLi (Gilman)** → ketone (Gilman gives ketone, not 3° alcohol).',
        hindi: '**एसिड क्लोराइड/नाइट्राइल + RMgX या R₂CuLi (गिलमैन) से** → कीटोन (गिलमैन कीटोन देता है, 3° अल्कोहल नहीं)।',
            },
            {
              english: '**Friedel-Crafts acylation**: $ArH + RCOCl \\xrightarrow{AlCl_3} ArCOR$.',
        hindi: '**फ़्रीडेल-क्राफ्ट्स एसाइलेशन**: $ArH + RCOCl \\xrightarrow{AlCl_3} ArCOR$।',
            },
            {
              english: '**Etard**: toluene + CrO₂Cl₂ → benzaldehyde via chromium complex.',
        hindi: '**एटार्ड**: टोल्यूनि + CrO₂Cl₂ → क्रोमियम कॉम्प्लेक्स के माध्यम से बेन्ज़ेल्डिहाइड।',
            },
            {
              english: '**Gattermann-Koch**: benzene + CO + HCl + AlCl₃/CuCl → benzaldehyde.',
        hindi: '**गैटरमैन-कोच**: बेंजीन + CO + HCl + AlCl₃/CuCl → बेंजाल्डिहाइड।',
            },
          ],
        },
      ],
    },
    {
      id: 'reactions-carbonyl',
      title: {
        english: 'Reactions of Aldehydes & Ketones',
        hindi: 'एल्डिहाइड और कीटोन की अभिक्रियाएँ',
      },
      blocks: [
        {
          type: 'list',
          items: [
            {
              english: '**Nucleophilic addition** to C=O. Reactivity: HCHO > RCHO > R₂CO (steric + +I of R reduces electrophilicity).',
        hindi: '**न्यूक्लियोफिलिक जोड़** C=O में। प्रतिक्रियाशीलता: HCHO > RCHO > R₂CO (R का स्टेरिक + +I इलेक्ट्रोफिलिसिटी को कम करता है)।',
            },
            {
              english: '• HCN → cyanohydrin (base catalysis).',
        hindi: '• एचसीएन → साइनोहाइड्रिन (बेस कैटेलिसिस)।',
            },
            {
              english: '• NaHSO₃ → bisulphite addition product (used in purification).',
        hindi: '• NaHSO₃ → बाइसल्फ़ाइट अतिरिक्त उत्पाद (शुद्धिकरण में प्रयुक्त)।',
            },
            {
              english: '• ROH (acid cat.) → hemiacetal → acetal.',
        hindi: '• आरओएच (एसिड कैट.) → हेमिसिएटल → एसीटल।',
            },
            {
              english: '• RNH₂ → imine (Schiff base); H₂N-OH → oxime; H₂N-NH₂ → hydrazone; 2,4-DNP → 2,4-DNP-hydrazone (orange ppt — test for >C=O).',
        hindi: '• RNH₂ → इमाइन (शिफ बेस); H₂N-OH → ऑक्सीम; H₂N-NH₂ → हाइड्रोज़ोन; 2,4-डीएनपी → 2,4-डीएनपी-हाइड्राज़ोन (नारंगी पीपीटी - >C=O) के लिए परीक्षण। **कमी**:',
            },
            {
              english: '**Reduction**:',
        hindi: '**कमी**:',
            },
            {
              english: '• To alcohol: $NaBH_4, LiAlH_4, H_2/Ni$.',
        hindi: '• शराब के लिए: $NaBH_4, LiAlH_4, H_2/Ni$।',
            },
            {
              english: '• To $-CH_2$- (Clemmensen): $Zn(Hg)/HCl$ — only for acid-stable.',
        hindi: '• $-CH_2$- (क्लेमेंसेन): $Zn(Hg)/HCl$ - केवल एसिड-स्थिर के लिए।',
            },
            {
              english: '• To $-CH_2$- (Wolff-Kishner): $NH_2NH_2 + KOH$ in glycol — only for base-stable.',
        hindi: '• $-CH_2$- (वोल्फ-किश्नर): $NH_2NH_2 + KOH$ ग्लाइकोल में - केवल बेस-स्टेबल के लिए। **ऑक्सीकरण**:',
            },
          ],
        },
        {
          type: 'list',
          items: [
            {
              english: '**Oxidation**:',
        hindi: '**ऑक्सीकरण**:',
            },
            {
              english: '• Tollens $[Ag(NH_3)_2]^+$ → silver mirror with RCHO (not ketones).',
        hindi: '• टॉलेंस $[Ag(NH_3)_2]^+$ → RCHO (कीटोन नहीं) के साथ चांदी का दर्पण।',
            },
            {
              english: '• Fehling (Cu²⁺ + tartrate) → red Cu₂O ppt with **aliphatic** RCHO; aromatic RCHO does NOT react.',
        hindi: '• फेहलिंग (Cu²⁺ + टार्ट्रेट) → **स्निग्ध** RCHO के साथ लाल Cu₂O पीपीटी; सुगंधित आरसीएचओ प्रतिक्रिया नहीं करता है।',
            },
            {
              english: '• Benedict — similar to Fehling.',
        hindi: '• बेनेडिक्ट - फेहलिंग के समान।',
            },
            {
              english: '• HCOOH and HCHO reduce both Tollens and Fehling.',
        hindi: '• HCOOH और HCHO टोलेंस और फेहलिंग दोनों को कम करते हैं।',
            },
            {
              english: '**Cannizzaro reaction**: aldehydes **without α-H** (HCHO, PhCHO, $(CH_3)_3CCHO$) + concentrated NaOH → alcohol + carboxylate (disproportionation).',
        hindi: '**कैनिज़ारो प्रतिक्रिया**: एल्डिहाइड **बिना α-H** (HCHO, PhCHO, $(CH_3)_3CCHO$) + सांद्र NaOH → अल्कोहल + कार्बोक्सिलेट (असमानता)।',
            },
            {
              english: '**Aldol condensation**: aldehydes/ketones with α-H + dilute NaOH → β-hydroxy carbonyl → on heating, α,β-unsaturated carbonyl.',
        hindi: '**एल्डोल संघनन**: aldehydes/ketones α-H + तनु NaOH → β-हाइड्रॉक्सी कार्बोनिल → गर्म करने पर, α,β-असंतृप्त कार्बोनिल के साथ।',
            },
            {
              english: '**Cross-aldol** between two different α-H carbonyls gives 4 products; cross-aldol between α-H and non-α-H (e.g. RCHO + PhCHO) is selective.',
        hindi: 'दो अलग-अलग α-H कार्बोनिल्स के बीच **क्रॉस-एल्डोल** 4 उत्पाद देता है; α-H और गैर-α-H (जैसे RCHO + PhCHO) के बीच क्रॉस-एल्डोल चयनात्मक है।',
            },
          ],
        },
        {
          type: 'list',
          items: [
            {
              english: '**Haloform reaction**: $CH_3CO\\text{-}R$ (methyl ketones) or $CH_3CH(OH)\\text{-}R$ (methyl carbinols) + $X_2/NaOH$ → $CHX_3$ + $RCOO^-Na^+$.',
        hindi: '**हेलोफॉर्म प्रतिक्रिया**: $CH_3CO\\text{-}R$ (मिथाइल कीटोन्स) या $CH_3CH(OH)\\text{-}R$ (मिथाइल कार्बिनोल) + $X_2/NaOH$ → $CHX_3$ + $RCOO^-Na^+$।',
            },
            {
              english: 'Iodoform test (yellow ppt): given by ethanol, all methyl ketones, isopropanol, lactic acid, etc.',
        hindi: 'आयोडोफॉर्म परीक्षण (पीला पीपीटी): इथेनॉल, सभी मिथाइल कीटोन, आइसोप्रोपेनॉल, लैक्टिक एसिड, आदि द्वारा दिया जाता है।',
            },
          ],
        },
      ],
    },
    {
      id: 'carboxylic-acids',
      title: {
        english: 'Carboxylic Acids — Preparation',
        hindi: 'कार्बोक्सिलिक अम्ल — तैयारी',
      },
      blocks: [
        {
          type: 'list',
          items: [
            {
              english: 'Oxidation of 1° alcohol or aldehyde (KMnO₄, K₂Cr₂O₇).',
        hindi: '1° अल्कोहल या एल्डिहाइड का ऑक्सीकरण (KMnO₄, K₂Cr₂O₇)।',
            },
            {
              english: 'Hydrolysis of nitrile, ester, amide.',
        hindi: 'नाइट्राइल, एस्टर, एमाइड का हाइड्रोलिसिस।',
            },
            {
              english: 'Carboxylation of Grignard: $R\\text{-MgX} + CO_2 \\xrightarrow{H_3O^+} R\\text{-COOH}$ (one C added).',
        hindi: 'ग्रिग्नार्ड का कार्बोक्सिलेशन: $R\\text{-MgX} + CO_2 \\xrightarrow{H_3O^+} R\\text{-COOH}$ (एक C जोड़ा गया)।',
            },
            {
              english: 'From alkyl benzene: side-chain oxidation by hot KMnO₄ converts any $-R$ ($R \\ne \\text{tert}$) to $-COOH$.',
        hindi: 'एल्काइल बेंजीन से: गर्म KMnO₄ द्वारा साइड-चेन ऑक्सीकरण किसी भी $-R$ ($R \\ne \\text{tert}$) को $-COOH$ में परिवर्तित करता है।',
            },
            {
              english: 'From acid derivatives by hydrolysis (acid chloride > anhydride > ester > amide).',
        hindi: 'हाइड्रोलिसिस द्वारा एसिड डेरिवेटिव से (एसिड क्लोराइड > एनहाइड्राइड > एस्टर > एमाइड)।',
            },
          ],
        },
      ],
    },
    {
      id: 'reactions-acids',
      title: {
        english: 'Carboxylic Acids — Acidity & Reactions',
        hindi: 'कार्बोक्सिलिक अम्ल — अम्लता और अभिक्रियाएँ',
      },
      blocks: [
        {
          type: 'list',
          items: [
            {
              english: 'Acidity: RCOOH ($pK_a \\approx 4$–5) much more than alcohol; –COO⁻ resonance-stabilised.',
        hindi: 'अम्लता: RCOOH ($pK_a \\approx 4$–5) शराब से कहीं अधिक; -COO⁻ अनुनाद-स्थिर।',
            },
            {
              english: 'Effect of substituents on acidity:',
        hindi: 'अम्लता पर पदार्थों का प्रभाव:',
            },
            {
              english: '• EWG (–Cl, –NO₂, –CN) increase $K_a$.',
        hindi: '• EWG (-Cl, -NO₂, -CN) $K_a$ बढ़ जाता है।',
            },
            {
              english: '• EDG (–CH₃, –OCH₃ in alkyl) decrease.',
        hindi: '• EDG (-CH₃, -OCH₃ एल्काइल में) में कमी।',
            },
            {
              english: '• Order: $HCOOH > CH_3COOH$; $FCH_2COOH > ClCH_2COOH > BrCH_2COOH > ICH_2COOH$.',
        hindi: '• आदेश: $HCOOH > CH_3COOH$; $FCH_2COOH > ClCH_2COOH > BrCH_2COOH > ICH_2COOH$.',
            },
            {
              english: '• Effect falls with distance (–I damped over chain): $\\alpha > \\beta > \\gamma$.',
        hindi: '• प्रभाव दूरी के साथ गिरता है (-मैंने श्रृंखला पर दबाव डाला): $\\alpha > \\beta > \\gamma$। सुगंधित: $\\alpha > \\beta > \\gamma$-प्रतिस्थापित बेंजोइक एसिड लगभग सभी बेंजोइक (**ऑर्थो प्रभाव**, स्टेरिक) से अधिक मजबूत होते हैं।',
            },
            {
              english: 'Aromatic: $o$-substituted benzoic acids almost all stronger than benzoic (**ortho effect**, steric).',
        hindi: 'सुगंधित: $o$-प्रतिस्थापित बेंजोइक एसिड लगभग सभी बेंजोइक (**ऑर्थो प्रभाव**, स्टेरिक) से अधिक मजबूत होते हैं।',
            },
            {
              english: 'Reactions:',
        hindi: 'प्रतिक्रियाएं:',
            },
            {
              english: '• With NaOH/Na₂CO₃/NaHCO₃: salt + H₂O (+ CO₂); CO₂ test distinguishes acid from phenol.',
        hindi: '• NaOH/Na₂CO₃/NaHCO₃ के साथ: नमक + H₂O (+ CO₂); CO₂ परीक्षण एसिड को फिनोल से अलग करता है।',
            },
            {
              english: '• Esterification (Fischer) with R\'OH/H⁺: nucleophilic acyl substitution.',
        hindi: '• R\\\'OH/H⁺ के साथ एस्टरीफिकेशन (फिशर): न्यूक्लियोफिलिक एसाइल प्रतिस्थापन।',
            },
            {
              english: '• Convert to acid chloride with $SOCl_2$ (best), $PCl_5$, or $PCl_3$.',
        hindi: '• $SOCl_2$ (सर्वोत्तम), $PCl_5$, या $PCl_3$ के साथ एसिड क्लोराइड में परिवर्तित करें।',
            },
            {
              english: '• Anhydride formation by P₂O₅ or heating with itself.',
        hindi: '• P₂O₅ द्वारा एनहाइड्राइड का निर्माण या स्वयं के साथ गर्म करना।',
            },
            {
              english: '• Reduction: $LiAlH_4$ → 1° alcohol; $B_2H_6$ → 1° alcohol selectively (no –NO₂/–CN reduction).',
        hindi: '• कमी: $LiAlH_4$ → 1° अल्कोहल; $B_2H_6$ → 1° अल्कोहल चयनात्मक रूप से (कोई -NO₂/-CN कमी नहीं)।',
            },
            {
              english: '• Decarboxylation: $RCOONa + NaOH/CaO \\xrightarrow{\\Delta} RH + Na_2CO_3$ (soda lime).',
        hindi: '• डीकार्बोक्सिलेशन: $RCOONa + NaOH/CaO \\xrightarrow{\\Delta} RH + Na_2CO_3$ (सोडा लाइम)।',
            },
            {
              english: '• **Hell-Volhard-Zelinsky (HVZ)**: $RCH_2COOH + Cl_2 / P \\to RCHCl\\text{COOH}$ — α-halogenation.',
        hindi: '• **हेल-वोलहार्ड-ज़ेलिंस्की (HVZ)**: $RCH_2COOH + Cl_2 / P \\to RCHCl\\text{COOH}$ — α-हैलोजनेशन।',
            },
            {
              english: '• **Kolbe electrolysis** of sodium carboxylate → alkane + 2CO₂.',
        hindi: '• सोडियम कार्बोक्सिलेट → एल्केन + 2CO₂ का **कोल्बे इलेक्ट्रोलिसिस**।',
            },
          ],
        },
      ],
    },
  ],
  keyFormulas: [
    {
      latex: 'RCOCl + H_2 \\xrightarrow{Pd/BaSO_4} RCHO',
      desc: {
        english: 'Rosenmund',
        hindi: 'रोसेनमुंड',
      },
    },
    {
      latex: 'R\\text{-MgX} + CO_2 \\xrightarrow{H_3O^+} R\\text{-COOH}',
      desc: {
        english: 'Grignard + CO₂',
        hindi: 'ग्रिग्नार्ड + CO₂',
      },
    },
    {
      latex: '2HCHO + NaOH \\to CH_3OH + HCOONa',
      desc: {
        english: 'Cannizzaro',
        hindi: 'कैनिज़ारो',
      },
    },
    {
      latex: '2RCHO \\xrightarrow{\\text{dil NaOH}} R\\text{-CH(OH)CH(R)CHO}',
      desc: {
        english: 'Aldol',
        hindi: 'एल्डोल',
      },
    },
    {
      latex: 'RCH_2COOH + X_2/P \\to RCHX\\text{-COOH}',
      desc: {
        english: 'Hell-Volhard-Zelinsky',
        hindi: 'हेल-वोल्हार्ड-ज़ेलिंस्की',
      },
    },
    {
      latex: 'CH_3COR + I_2/NaOH \\to CHI_3\\downarrow + RCOO^-',
      desc: {
        english: 'Iodoform',
        hindi: 'आयोडोफॉर्म',
      },
    },
    {
      latex: 'RCOONa + NaOH/CaO \\xrightarrow{\\Delta} RH + Na_2CO_3',
      desc: {
        english: 'Decarboxylation',
        hindi: 'डीकार्बोक्सिलेशन',
      },
    },
  ],
  pyqs: [
    {
      year: 2024,
      exam: 'JEE Main',
      tag: 'Cannizzaro',
      problem: {
        english: 'Which undergoes Cannizzaro reaction?',
        hindi: 'कौन सा कैनिज़ारो प्रतिक्रिया से गुजरता है?',
      },
      answer: {
        english: 'HCHO (no α-H)',
        hindi: 'HCHO (कोई α-H नहीं)',
      },
      solution: {
        english: 'Requires absence of α-hydrogen.',
        hindi: 'α-हाइड्रोजन की अनुपस्थिति की आवश्यकता है।',
      },
    },
    {
      year: 2023,
      exam: 'NEET',
      tag: 'Acidity',
      problem: {
        english: 'Strongest acid:',
        hindi: 'सबसे मजबूत अम्ल:',
      },
      answer: {
        english: 'FCH₂COOH',
        hindi: 'FCH₂COOH',
      },
      solution: {
        english: 'F has greatest –I effect.',
        hindi: 'F का -I प्रभाव सबसे अधिक है।',
      },
    },
    {
      year: 2022,
      exam: 'JEE Main',
      tag: 'Test',
      problem: {
        english: 'Tollens reagent gives silver mirror with:',
        hindi: 'टॉलेंस अभिकर्मक चांदी का दर्पण देता है:',
      },
      answer: {
        english: 'Aldehydes (and HCOOH)',
        hindi: 'एल्डिहाइड (और HCOOH)',
      },
      solution: {
        english: 'Ketones do not reduce Ag⁺.',
        hindi: 'केटोन्स Ag⁺ को कम नहीं करते हैं।',
      },
    },
    {
      year: 2021,
      exam: 'NEET',
      tag: 'Iodoform',
      problem: {
        english: 'Compound NOT giving iodoform test:',
        hindi: 'यौगिक आयोडोफॉर्म परीक्षण नहीं दे रहा:',
      },
      answer: {
        english: '3-Pentanone',
        hindi: '3-पेंटानोन',
      },
      solution: {
        english: 'No CH₃CO– or CH₃CH(OH)– group.',
        hindi: 'कोई CH₃CO- या CH₃CH(OH)- समूह नहीं।',
      },
    },
    {
      year: 2020,
      exam: 'JEE Main',
      tag: 'Reduction',
      problem: {
        english: 'Clemmensen reduction reagent:',
        hindi: 'क्लेमेंसेन कमी अभिकर्मक:',
      },
      answer: {
        english: 'Zn(Hg) / conc. HCl',
        hindi: 'Zn(Hg) / सान्द्रता। एचसीएल',
      },
      solution: {
        english: 'Acidic medium reduces C=O to CH₂.',
        hindi: 'अम्लीय माध्यम C=O to CH₂ को कम करता है।',
      },
    },
  ],
  next: {
    slug: 'amines',
    title: 'Amines',
  },
};

export default chapter;
